import React, {useCallback, useContext, useEffect, useState} from "react";
import {AButton, ADataTable, APagination} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {t} from "i18next";
import {useDispatch, useSelector} from "react-redux";
import AssigneesWidget from "components/AssigneeWidget/AssigneeWidget";
import {IncidentsContext} from "contexts/IncidentsContext";
import {setDrawerIncident} from "store/features/incidentsSlice";
import {
  selectIncidentSearchText,
  selectIncidentsLoading,
  selectIncidentTotalRows,
  selectIncidentZeroResults,
  selectLoadedIncidents
} from "store/selectors";
import {sendIncidentsListTitleTelemetry} from "api/telemetry/incidentsTelemetrics";
import {FieldNames} from "models/fields";
import {tableLoadingState} from "utils/dataTable";
import {getShortId} from "utils/misc";
import TableEmptyState from "../../../components/Table/TableEmptyState";
import TooltipDelay from "../../../components/TooltipDelay";
import {INCIDENT_FILTER_SORT_DEFAULT} from "../../../models/options";
import PriorityScore from "../PriorityScore/PriorityScore";
import BulkAction from "./BulkAction";
import IncidentTableCheckbox from "./IncidentTableCheckbox";
import IncidentTableCreatedDate from "./IncidentTableCreatedDate";
import StatusDropdownMenu from "./StatusDropdownMenu";
import css from "./IncidentsTable.module.scss";

const IncidentsTable = ({openDrawer}) => {
  const dispatch = useDispatch();
  const incidents = useSelector(selectLoadedIncidents);
  const isLoading = useSelector(selectIncidentsLoading);
  const totalIncidentCount = useSelector(selectIncidentTotalRows);
  const hasZeroIncidents = useSelector(selectIncidentZeroResults);
  const searchText = useSelector(selectIncidentSearchText);
  const {
    page,
    setPage,
    filterSettings,
    dispatchFilterSettings,
    pageCount,
    setPageCount
  } = useContext(IncidentsContext);
  const {statusFilter, assigneeFilter, dateRangeFilter} = filterSettings;

  const isEmpty = !isLoading && hasZeroIncidents;

  const hasFiltersApplied = [
    statusFilter.length > 0,
    assigneeFilter.length > 0,
    dateRangeFilter.filter !==
      INCIDENT_FILTER_SORT_DEFAULT.dateRangeFilter.filter,
    searchText.length > 0
  ].some((isApplied) => isApplied);

  const [tableModel, setTableModel] = useState([...incidents]);

  useEffect(() => {
    setTableModel([...incidents]);
  }, [incidents]);

  const handleSingleRowChecked = (rowData) => {
    setTableModel(
      tableModel.map((incident) =>
        incident.id !== rowData.id
          ? incident
          : {...incident, checked: !rowData.checked}
      )
    );
  };

  const handleBulkActionChecked = (checked) => {
    setTableModel(
      tableModel.map((incident) => ({...incident, checked: checked}))
    );
  };

  const handleTitleClick = (incident) => {
    const {id, severity, short_description, source, status, title} = incident;

    // Make sure to dispatch when "promised" that there is no drawer
    // currently opened in the UI to prevent a flickering state.
    openDrawer().then(() => dispatch(setDrawerIncident(incident)));
    sendIncidentsListTitleTelemetry(
      `${getShortId(id)}`,
      severity,
      short_description,
      source,
      status,
      title
    );
  };

  const headers = [
    {
      name: (
        <BulkAction
          checked={
            !!tableModel.length &&
            tableModel.every((incident) => incident.checked)
          }
          indeterminate={
            !!tableModel.length &&
            tableModel.some((incident) => incident.checked)
          }
          disabled={isLoading || hasZeroIncidents}
          incidents={tableModel.filter((incident) => incident.checked)}
          handleOnSelect={handleBulkActionChecked}
        />
      ),
      className: css.cellCheckboxAction,
      cell: {
        component: (rowData) => (
          <IncidentTableCheckbox
            checked={rowData.checked}
            disabled={isLoading}
            handleClick={() => handleSingleRowChecked(rowData)}
          />
        )
      }
    },
    {
      name: t("incidents.table.priority"),
      key: FieldNames.scoresGlobal,
      align: "start",
      sortable: true,
      sort: false,
      className: classNames(css.cellChip),
      cell: {
        component: (incident) => (
          <PriorityScore showLabel={false} {...{incident}} />
        )
      }
    },
    {
      name: "Name",
      key: FieldNames.title,
      align: "start",
      sortable: true,
      sort: false,
      className: classNames(css.cellTitle),
      cell: {
        className: css.truncateCellText,
        component: (incident) => {
          return (
            <TooltipDelay
              title={t("incidents.table.name")}
              subtitle={incident?.title}>
              <AButton
                onClick={() => handleTitleClick(incident)}
                className={classNames(css.incidentsNameColBtn, "px-0 d-inline")}
                tertiary>
                {incident?.title}
              </AButton>
            </TooltipDelay>
          );
        }
      }
    },
    {
      name: (
        <TooltipDelay title={t("incidents.table.source")}>
          <div className={"text-truncate"}>
            <p className={css.truncatedContents}>
              {t("incidents.table.source")}
            </p>
          </div>
        </TooltipDelay>
      ),
      key: FieldNames.source,
      align: "start",
      sortable: true,
      sort: false,
      className: classNames(css.cellSource),
      cell: {
        className: css.truncateCellText,
        component: ({source}) => (
          <TooltipDelay title={t("incidents.table.source")} subtitle={source}>
            <div className={"text-truncate"}>
              <p className={css.truncatedContents}>{source}</p>
            </div>
          </TooltipDelay>
        )
      }
    },
    {
      name: (
        <TooltipDelay title={t("incidents.table.created")}>
          <div className={"text-truncate"}>
            <p className={css.truncatedContents}>
              {t("incidents.table.created")}
            </p>
          </div>
        </TooltipDelay>
      ),
      key: FieldNames.incidentTimeOpened,
      sortable: true,
      sort: false,
      className: classNames(css.cellCreated),
      cell: {
        className: css.truncateCellText,
        component: ({incident_time}) => (
          <IncidentTableCreatedDate date={incident_time.opened} />
        )
      }
    },
    {
      name: (
        <TooltipDelay title={t("incidents.table.assigned")}>
          <div className={"text-truncate"}>
            <p className={css.truncatedContents}>
              {t("incidents.table.assigned")}
            </p>
          </div>
        </TooltipDelay>
      ),
      key: FieldNames.assignees,
      sortable: false,
      sort: false,
      className: classNames(css.cellAssigned, css.truncateCellText),
      cell: {
        className: css.truncateCellText,
        component: (rowData) => (
          <AssigneesWidget
            showLabel={false}
            key={rowData.id}
            incident={rowData}
          />
        )
      }
    },
    {
      name: t("incidents.table.status"),
      key: FieldNames.status,
      align: "start",
      sortable: true,
      sort: false,
      className: classNames(css.cellStatus),
      cell: {
        component: (rowData) => (
          <StatusDropdownMenu
            incident={rowData}
            truncate
            useMount
            chipProps={{
              small: false
            }}
            menuPlacement="top-left"
          />
        )
      }
    }
  ];

  const items = isLoading ? tableLoadingState(pageCount) : tableModel;

  const handleSortChange = useCallback(
    /**
     * @param {{key: string, direction: "asc"|"desc"}} sort
     */
    ({direction, key}) => {
      dispatchFilterSettings({order: direction, orderBy: key});
    },
    [dispatchFilterSettings]
  );

  return (
    <div className={classNames("incident-table")}>
      {isEmpty && (
        <TableEmptyState
          entityNamePlural={t("incidents.title")}
          hasFiltersApplied={hasFiltersApplied}
        />
      )}

      {!isEmpty && (
        <>
          <ADataTable
            data-testid={"IncidentsTable-DataTable"}
            tight
            headers={headers}
            truncateHeaders
            items={items}
            className={classNames("mx-auto", css.incidentsListTable)}
            sort={{
              key: filterSettings.orderBy,
              direction: filterSettings.order
            }}
            onSort={handleSortChange}
            disableSortReset
            disabled={isLoading || hasZeroIncidents}
            isRowSelected={(rowData) => rowData?.checked}
          />
          <div className="d-flex justify-end">
            <APagination
              className="mt-2"
              disabled={isLoading}
              localeFormatting
              onPageChange={(value) => setPage(value)}
              onResultsPerPageChange={(value) => setPageCount(value)}
              page={page}
              resultsPerPage={pageCount}
              total={totalIncidentCount}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default IncidentsTable;
